import React, { useEffect, useRef } from 'react'
import { Tooltip, useTheme } from '@mui/material'
import { TooltipProps } from '@mui/material/Tooltip/Tooltip'
import { makeStyles } from '@mui/styles'

import { ClassesType } from '../types'
import { scrollNodeIntoViewIfNeeded } from '../utils'
import Text from './Text'

const useStyles = makeStyles(
  theme => ({
    errorPopper: {
      zIndex: theme.zIndex.modal,
      opacity: 1,
    },
    errorTooltip: {
      marginTop: '0 !important',
      width: 277,
      backgroundColor: (props: UseStylesProps) => props.errorColor,
      boxShadow: '0 2px 5px 0 rgba(0,0,0,0.30)',
    },
    errorTooltipText: {
      color: theme.colors.profileText,
      overflowWrap: 'break-word',
    },
    errorArrow: {
      color: (props: UseStylesProps) => props.errorColor,
    },
  }),
  { name: 'ErrorTooltip' },
)

export interface ErrorTooltipProps
  extends Omit<TooltipProps, 'classes' | 'title'> {
  readonly classes?: ClassesType<typeof useStyles>
  readonly disablePortal?: boolean
  readonly errorColor?: string
  readonly message?: React.ReactNode
  readonly title?: React.ReactNode
  readonly validateTag?: number
}

interface UseStylesProps {
  classes?: ClassesType<typeof useStyles>
  errorColor?: ErrorTooltipProps['errorColor']
}

const ErrorTooltip = ({
  classes: classesProp,
  message = '',
  children,
  open = false,
  disablePortal = false,
  validateTag,
  errorColor,
  title,
  ...rest
}: ErrorTooltipProps) => {
  const theme = useTheme()
  const useStylesProps: UseStylesProps = {
    errorColor: errorColor || theme.colors.errorColor,
    classes: classesProp,
  }
  const classes = useStyles(useStylesProps)
  const tooltipRef = useRef()

  useEffect(() => {
    if (open && message && tooltipRef.current) {
      scrollNodeIntoViewIfNeeded(tooltipRef.current)
    }
  }, [validateTag])

  return (
    <Tooltip
      arrow
      PopperProps={{
        disablePortal,
        popperOptions: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 14],
              },
            },
            {
              name: 'flip',
              enabled: false,
            },
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                altBoundary: true,
              },
            },
          ],
        },
      }}
      classes={{
        popper: classes.errorPopper,
        tooltip: classes.errorTooltip,
        arrow: classes.errorArrow,
      }}
      open={open}
      ref={tooltipRef}
      title={
        title || (
          <Text
            align="center"
            className={classes.errorTooltipText}
            mx={1.5}
            variant="body"
          >
            {message}
          </Text>
        )
      }
      {...rest}
    >
      {children}
    </Tooltip>
  )
}

export default ErrorTooltip
